// ./src/utils/api/VenueCategoryService.ts
//
// Venue category (global, read-only vocabulary used to classify venues) service.
// Public — no auth required. Used to populate the category multi-select in the
// venue drawer / filter / bulk modals. OpResult-native.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api VENUE_CATEGORY_GROUP boundary)
// =============================================================================

export const VENUE_CATEGORY_GROUP_VALUES = ["place", "service"] as const;
export type VenueCategoryGroup = (typeof VENUE_CATEGORY_GROUP_VALUES)[number];

// =============================================================================
// GraphQL
// =============================================================================

const VENUE_CATEGORY_FIELDS = `
  id
  code
  name
  categoryGroup
  icon
  status
`;

const VENUE_CATEGORY_LIST_QUERY = gql`
  query VenueCategoryList($filter: VenueCategoryFilter, $params: PaginationAndSorting) {
    venueCategoryList(filter: $filter, params: $params) {
      code
      data { ${VENUE_CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api VenueCategory schema)
// =============================================================================

export interface VenueCategory {
  id?: string;
  code?: string | null;
  name?: string | null;
  categoryGroup?: string | null;
  icon?: string | null;
  status?: number;
}

export interface VenueCategoryFilter {
  id?: string[] | null;
  code?: string[] | null;
  categoryGroup?: string[] | null;
  searchKeyword?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class VenueCategoryService {
  static async list(
    filter?: VenueCategoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(VENUE_CATEGORY_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }
}
